import {
  Panel,
  PanelAction,
  RoutedPanelProps,
} from '@/components/Layout/Panel/Panel';
import { Tabs, TabsContent, TabsLink, TabsList } from '@/components/ui/tabs';
import { ChecklistTable } from '@/features/checklists/checklists.table';
import { useWorkChecklists } from '@/features/lists/lists.hooks';
import { TicketTable } from '@/features/tickets/ticket.table';
import { useWorkTickets } from '@/features/tickets/tickets.hooks';
import {
  TicketReportProvider,
  useTicketReportContext,
} from '@/hooks/useTicketReportContext';
import { Work } from '@certhon/models';
import { FileDown } from 'lucide-react';
import { useMemo } from 'react';
import { useWork } from './works.hooks';
import { getWorkActions, adaptActionsForPanel } from './works.actions';

function getWorkDescription(work: Work) {
  return `${work.id} - ${work.oms}`;
}

function WorkChecklists({
  workId,
  basePath,
}: {
  workId: number;
  basePath: string;
}) {
  const res = useWorkChecklists(workId);
  return <ChecklistTable uriPrefix={basePath} {...res} />;
}

function WorkTickets({
  workId,
  projectId,
  basePath,
}: {
  workId: number;
  projectId: number;
  basePath: string;
}) {
  const res = useWorkTickets(workId);
  return (
    <TicketTable
      projectId={projectId}
      uriPrefix={`${basePath}/tickets`}
      {...res}
    />
  );
}

/**
 * Inner component that has access to TicketReportContext
 */
function WorkDetailsPanelInner({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) {
  const { workId: workIdString, tab, projectId: projectIdString } = params;
  const workId = parseInt(workIdString);
  const projectId = parseInt(projectIdString);
  const { data: work } = useWork(workId);
  const reportContext = useTicketReportContext();

  const title = !work ? `Work ${workIdString}` : getWorkDescription(work);

  const actions = useMemo(() => {
    const panelActions: PanelAction[] = [];

    // Add base work actions
    if (work) {
      const rowActions = getWorkActions();
      panelActions.push(...adaptActionsForPanel(rowActions, work));
    }

    // Add PDF report action (uses filtered tickets from context)
    if (reportContext) {
      panelActions.push({
        icon: FileDown,
        label: 'Tickets PDF',
        sublabel:
          reportContext.filteredTicketRecnums.length > 0
            ? `${reportContext.filteredTicketRecnums.length} tickets`
            : null,
        onCalled: reportContext.downloadFilteredTicketsPdf,
        disabled:
          reportContext.isGeneratingPdf ||
          reportContext.filteredTicketRecnums.length === 0,
      });
    }

    return panelActions;
  }, [work, reportContext]);

  return (
    <Panel {...panelProps} title={title} actions={actions}>
      <Tabs value={tab || 'tickets'} className="w-full">
        <TabsList className="w-full">
          <TabsLink to={`${basePath}/tickets`} value="tickets">
            Tickets
          </TabsLink>
          <TabsLink to={`${basePath}/lists`} value="lists">
            Lists
          </TabsLink>
        </TabsList>
        <TabsContent value="tickets">
          <WorkTickets
            basePath={basePath}
            workId={workId}
            projectId={projectId}
          />
        </TabsContent>
        <TabsContent value="lists">
          <WorkChecklists basePath={basePath} workId={workId} />
        </TabsContent>
      </Tabs>
    </Panel>
  );
}

/**
 * Work details panel with PDF report capability.
 * Wraps content in TicketReportProvider to enable filtered ticket PDF generation.
 */
export const WorkDetailsPanel = (props: RoutedPanelProps) => {
  const workId = parseInt(props.params.workId);
  const projectId = parseInt(props.params.projectId);
  return (
    <TicketReportProvider projectId={projectId} workId={workId}>
      <WorkDetailsPanelInner {...props} />
    </TicketReportProvider>
  );
};
